<template>
  <!-- используем form чтобы обертка не влияла на стили -->
  <slot />
</template>

<script>

export default {
  name: "VForm",

  data() {
    return {
      controls:        new Map(),
      validationState: new Map(),
    };
  },

  computed: {
    isValid() {
      for (const [, value] of this.validationState) {
        if (value.isValid === false) return false;
      }

      return true;
    },

    invalidControls() {
      const result = new Map();

      for (const [key, value] of this.validationState) {
        if (value.isValid === false) result.set(key, this.controls.get(key));
      }

      return result;
    },

    /**
     * Плоский список ошибок всех невалидных контролов в виде
     * { message: string, fieldRef: ComponentInstance }.
     *
     * Один контрол может дать несколько записей (если у него несколько ошибок
     * в validation.errors). Порядок — порядок регистрации контролов в форме.
     *
     * Используется потребителем (например VDialogV3) для показа сводной плашки
     * с возможностью клика по ошибке — клик зовёт scrollToControl(fieldRef).
     */
    errors() {
      const result = [];

      for (const [key, validation] of this.validationState) {
        if (validation.isValid === false) {
          const control = this.controls.get(key);
          for (const message of validation.errors) {
            result.push({ message, fieldRef: control });
          }
        }
      }

      return result;
    },

    /**
     * Плоский список предупреждений всех контролов (поле validation.warnings).
     * В отличие от errors, формируется независимо от validation.isValid —
     * предупреждения показываются даже когда форма валидна. Структура та же:
     * { message: string, fieldRef: ComponentInstance }, чтобы потребитель
     * мог использовать одинаковый рендер плашек для ошибок и предупреждений.
     */
    warnings() {
      const result = [];

      for (const [key, validation] of this.validationState) {
        if (validation.warnings && validation.warnings.length > 0) {
          const control = this.controls.get(key);
          for (const message of validation.warnings) {
            result.push({ message, fieldRef: control });
          }
        }
      }

      return result;
    },
  },

  methods: {
    registerControl(control) {
      /** нужны только компоненты, которые являются контролерами */
      if (
        control.$props &&
        (control.$props.value !== undefined ||
          control.$options.model ||
          control.isControl)
      ) {
        if (this.controls.has(control.uid)) return;

        this.controls.set(control.uid, control);

        this.updateValidationState(control);
      }
    },

    unregisterControl(control) {
      if (!this.controls.has(control.uid)) return;

      this.controls.delete(control.uid);
      this.updateValidationState(control);
    },

    /** Если нужно точечно что-то обновить, то передаёшь контрол который хочешь синкануть,
     * если нужно обновить всё состояние, то ничего не передаёшь */
    updateValidationState(control = null) {
      if (control) {
        if (this.controls.has(control.uid)) {
          this.validationState.set(control.uid, control.validation);
        } else {
          this.validationState.delete(control.uid);
        }
      } else {
        for (const [key, value] of this.controls) {
          this.validationState.set(key, value.validation);
        }
      }
    },

    /**
     * Запускает валидацию всех контролов.
     * Возвращает промис, который резолвится когда ВСЕ проверки завершены,
     * включая асинхронные (например серверная проверка уникальности).
     *
     * Каждый контрол сам await'ит свою валидацию внутри validateForce,
     * VForm здесь собирает их промисы и ждёт через Promise.all.
     */
    async validate() {
      /** Важно занулить состояние, потому что Map не реактивный, а когда ты таким образом сбрасываешь
       * состояние, то вью реагирует */
      this.validationState = new Map();

      // Запускаем validateForce на каждом контроле и собираем промисы.
      // validateForce — async (помечает touched+dirty и await'ит validation.validate),
      // поэтому возвращает Promise, который Promise.all ниже дождётся целиком.
      const promises = [];
      for (const [, control] of this.controls) {
        if (typeof control.validateForce === 'function') {
          promises.push(control.validateForce());
        }
      }

      await Promise.all(promises);

      this.updateValidationState();
    },

    /** пример: добавили новый пароль и нужно стереть текущее значение,
     * но не провоцировать новой required ошибки */
    reset() {
      this.validationState = new Map();

      for (const [, control] of this.controls) {
        control.validationReset();
      }
    },

    /**
     * Скроллит к указанному контролу (или DOM-элементу) и фокусирует
     * первый интерактивный элемент внутри. Принимает либо инстанс компонента
     * (берётся его $el), либо сразу HTMLElement.
     *
     * Если передано что-то без $el / не HTMLElement — ничего не делает.
     */
    scrollToControl(fieldRef) {
      let el = null;
      if (fieldRef instanceof HTMLElement) {
        el = fieldRef;
      } else if (fieldRef && fieldRef.$el instanceof HTMLElement) {
        el = fieldRef.$el;
      } else {
        // не контрол и не элемент — пропускаем
      }

      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // фокусим интерактивный элемент внутри контрола, если он есть
        // if (el.matches('input, textarea, select')) {
        // 	el.focus();
        // } else {
        // 	const focusable = el.querySelector('input, textarea, select');
        // 	if (focusable) {
        // 		focusable.focus();
        // 	}
        // }
      } else {
        // нечего скроллить
      }
    },

    /**
     * Скроллит к первому невалидному контролу и фокусирует его.
     * Порядок — порядок регистрации контролов в форме (по DOM, сверху вниз).
     * Если невалидных контролов нет — ничего не делает.
     */
    scrollToFirstError() {
      const control = this.getFirstErrorControl();
      if (control === null) {
        // невалидных полей нет — нечего скроллить
      } else {
        this.scrollToControl(control);
      }
    },

    /**
     * Первый невалидный контрол (по порядку регистрации, т.е. сверху вниз по DOM).
     * null — если невалидных нет. Используется потребителем (VDialogV3), чтобы
     * найти вкладку с ошибкой и скроллить к полю.
     */
    getFirstErrorControl() {
      if (this.invalidControls.size === 0) {
        return null;
      } else {
        return this.invalidControls.values().next().value;
      }
    },
  },

};
</script>
